import type { CreatedStaff } from './api';

/**
 * The paper side of the staff import: one sign-in slip per new person, and the
 * same list as a file. Built here, in the browser, because the temporary
 * passwords exist nowhere else: the server returned them once and kept none.
 */

/** The import's columns, with one example row, for NKS to fill in. */
export const STAFF_TEMPLATE =
  'employee_no,name,username,email,phone,roles,branches\r\n' +
  'E-001,Ahmad bin Ismail,ahmad,ahmad@example.com,012-345 6789,"Sales Advisor; Cashier",KL\r\n';

const escapeHtml = (s: string): string =>
  s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c);

/**
 * One cell. A name from HR that starts like a formula is prefixed with a quote,
 * as the API's exports do: in Excel it could read the password cell beside it.
 */
const csvCell = (s: string): string => {
  const guarded = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\r\n]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

export function slipsCsv(staff: CreatedStaff[]): string {
  const rows = staff.map((s) =>
    [s.employeeNo, s.name, s.username, s.temporaryPassword, s.roles.join('; '), s.branches.join('; ')]
      .map(csvCell)
      .join(','),
  );
  return ['employee_no,name,username,temporary_password,roles,branches', ...rows].join('\r\n') + '\r\n';
}

export interface SlipWords {
  heading: string;
  address: string;
  username: string;
  password: string;
  roles: string;
  firstSignIn: string;
}

/** A self-contained page of slips, cut lines between them, black on white. */
export function slipsHtml(staff: CreatedStaff[], words: SlipWords, origin: string): string {
  const slips = staff
    .map(
      (s) => `<section class="slip">
  <h2>${escapeHtml(s.name)} <small>${escapeHtml(s.employeeNo)}</small></h2>
  <p>${escapeHtml(words.heading)}</p>
  <dl>
    <dt>${escapeHtml(words.address)}</dt><dd>${escapeHtml(origin)}</dd>
    <dt>${escapeHtml(words.username)}</dt><dd class="mono">${escapeHtml(s.username)}</dd>
    <dt>${escapeHtml(words.password)}</dt><dd class="mono">${escapeHtml(s.temporaryPassword)}</dd>
    <dt>${escapeHtml(words.roles)}</dt><dd>${escapeHtml(s.roles.join(', '))}</dd>
  </dl>
  <p class="note">${escapeHtml(words.firstSignIn)}</p>
</section>`,
    )
    .join('\n');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(words.heading)}</title><style>
body{font-family:-apple-system,'Segoe UI',Roboto,sans-serif;color:black;background:white;margin:0}
.slip{padding:16px 20px;border-bottom:1px dashed black;break-inside:avoid}
h2{font-size:16px;margin:0 0 4px}small{font-weight:400;color:dimgray}
p{margin:0 0 8px;font-size:12px}dl{display:grid;grid-template-columns:auto 1fr;gap:4px 12px;margin:0 0 8px;font-size:13px}
dt{color:dimgray}dd{margin:0}.mono{font-family:ui-monospace,Menlo,Consolas,monospace;font-size:15px;letter-spacing:.05em}
.note{font-style:italic}
</style></head><body>${slips}</body></html>`;
}

/**
 * Prints the slips from a hidden frame, so the app's own print layer (built for
 * documents the server renders) is not involved and nothing leaves the page.
 */
export function printSlips(html: string): void {
  const frame = document.createElement('iframe');
  frame.style.position = 'fixed';
  frame.style.width = '0';
  frame.style.height = '0';
  frame.style.border = '0';
  frame.onload = () => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
  };
  frame.srcdoc = html;
  document.body.appendChild(frame);
  // Long enough for the dialog to have taken its copy on every browser.
  setTimeout(() => {
    frame.remove();
  }, 60_000);
}

export function downloadText(filename: string, text: string): void {
  // The byte-order mark tells Excel on Windows it is UTF-8, so Chinese and
  // Tamil names come out as names.
  const url = URL.createObjectURL(new Blob(['\uFEFF', text], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 1000);
}
